/**
 * HpAbovePercentCondition - behavior tree condition checking HP percentage.
 */

import { CharacterStatsComponent } from '../../../../components/CharacterStatsComponent.js';
import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Actor } from '../../../../actors/Actor.js';
import type { Blackboard } from '../../blackboard/Blackboard.js';

export interface HpAbovePercentConditionOptions extends ConditionEvaluatorOptions {
  percentage?: number;
  percentageKey?: string;
  targetActorKey?: string;
  checkOwner?: boolean;
}

@EngineClass('HpAbovePercentCondition')
export class HpAbovePercentCondition extends ConditionEvaluator {
  private percentage: number;
  private percentageKey?: string;
  private targetActorKey?: string;
  private checkOwner: boolean;

  constructor(options: HpAbovePercentConditionOptions = {}) {
    super({
      name: 'HpAbovePercent',
      ...options
    });

    this.percentage = options.percentage ?? 50.0;
    this.percentageKey = options.percentageKey;
    this.targetActorKey = options.targetActorKey;
    this.checkOwner = options.checkOwner ?? true;
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    // Determine which actor to check
    let actorToCheck: Actor | null = null;

    if (this.checkOwner) {
      actorToCheck = this.getOwner(blackboard);
    } else {
      // Check target actor
      if (this.targetActorKey) {
        actorToCheck = blackboard.getValue<Actor>(this.targetActorKey) ?? null;
      }
    }

    if (!actorToCheck) {
      return BehaviorStatus.Failure;
    }

    // Get the actor's stats component
    const statsComponent = actorToCheck.getComponent(CharacterStatsComponent);
    if (!statsComponent) {
      return BehaviorStatus.Failure;
    }

    // Get the threshold percentage
    let thresholdPercent = this.percentage;
    if (this.percentageKey) {
      const percentFromBlackboard = blackboard.getValue<number>(this.percentageKey);
      if (percentFromBlackboard !== undefined) {
        thresholdPercent = percentFromBlackboard;
      }
    }

    // Calculate current HP percentage
    const currentHp = statsComponent.getCurrentHealth();
    const maxHp = statsComponent.getMaxHealth();
    const currentPercent = (currentHp / maxHp) * 100;

    // Check if HP is above threshold
    return currentPercent > thresholdPercent ? BehaviorStatus.Success : BehaviorStatus.Failure;
  }
}